/**
 * Container tab bar for selecting between containers in a pod sandbox.
 * Used in terminal, file explorer, and processes panels.
 *
 * @module components/ui/ContainerTabBar
 */

import { clsx } from 'clsx';

export interface ContainerTabBarProps {
  /** Container names from sourcesRef.pod.containers */
  containers: string[];
  /** Currently selected container name */
  selected: string;
  /** Callback when a container tab is selected */
  onSelect: (name: string) => void;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Tab bar for switching between containers in a pod sandbox.
 * Hidden automatically when there is only one container.
 */
export function ContainerTabBar({
  containers,
  selected,
  onSelect,
  className,
}: ContainerTabBarProps) {
  if (containers.length <= 1) return null;

  return (
    <div
      className={clsx(
        'flex items-center gap-1 px-3 py-1.5 border-b border-gray-200 dark:border-[#2a2a2a] bg-gray-50 dark:bg-[#111]',
        className
      )}
    >
      {containers.map((name) => (
        <button
          key={name}
          type="button"
          onClick={() => onSelect(name)}
          className={clsx(
            'px-3 py-1 text-xs font-medium rounded-md transition-colors',
            selected === name
              ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
              : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-[#1a1a1a]'
          )}
        >
          {name}
        </button>
      ))}
    </div>
  );
}
